
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, forwardRef, Input, Optional, Output, SkipSelf } from '@angular/core';
import { AbstractControl, ControlContainer, ControlValueAccessor, FormGroup, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { SHARED_DIRECTIVES } from '../../utils/shared-directives';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FieldErrorMessageComponent } from '../forms/field-error-message/field-error-message.component';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  standalone: true,
  selector: 'app-generic-datepicker',
  templateUrl: './generic-datepicker.component.html',
  styleUrl: './generic-datepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule,
    FlexLayoutModule,
    MatIconModule, 
    SHARED_DIRECTIVES, 
    DatePickerModule, 
    FloatLabelModule, 
    ReactiveFormsModule, 
    FormsModule,
    FieldErrorMessageComponent],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GenericDatepickerComponent),
    multi: true
  },
  {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => GenericDatepickerComponent),
    multi: true
  }],
})
export class GenericDatepickerComponent implements ControlValueAccessor, Validator {

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() formControlName: string = '';
  @Input() width: string = '100%';
  @Input() isStartDate: boolean = false;
  @Input() isEndDate: boolean = false;

  @Input()
  set minDate(value: Date | string | null | undefined) {
    this._minDate = this.parseDate(value);
  }

  get minDate(): Date | null {
    return this._minDate;
  }

  @Input()
  set maxDate(value: Date | string | null | undefined) {
    this._maxDate = this.parseDate(value);
  }

  get maxDate(): Date | null {
    return this._maxDate;
  }

  @Output() dateChanged = new EventEmitter<Date>();

  private _minDate: Date | null = null;
  private _maxDate: Date | null = null;

  value: Date | null = null;

  constructor(@Optional() @SkipSelf() private controlContainer: ControlContainer,
    private el: ElementRef) { }

  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  onValidatorChange: () => void = () => { };


  onChange = (value: any) => { };
  onTouched = () => { };

  writeValue(value: any): void {
    if (!value) {
      this.value = null;
      return;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      this.value = new Date(year, month - 1, day);
      return;
    }

    this.value = new Date(value);
  }

  private parseDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(event: any) {
    this.value = event.target.value;
    this.onChange(this.value);
  }

  onCalendarChange(value: Date) {
    this.value = value;
    this.onChange(this.value);
    this.dateChanged.emit(value);
  }

  aplicarMascara(event: any) {
    let valor = event.target.value;

    valor = valor.replace(/\D/g, "");

    if (valor.length > 2) {
      valor = valor.replace(/^(\d{2})(\d)/, "$1/$2");
    }
    if (valor.length > 5) {
      valor = valor.replace(/^(\d{2})\/(\d{2})(\d)/, "$1/$2/$3");
    }
    if (valor.length > 10) {
      valor = valor.substring(0, 10);
    }

    event.target.value = valor;
    if (valor.length === 10) {
      const [dia, mes, ano] = valor.split('/');
      const data = new Date(+ano, +mes - 1, +dia);

      this.value = data;
      this.onChange(this.value);
    } else {
      this.value = null;
      this.onChange(this.value);
    }
  }

  get control(): AbstractControl | null {
    if (!this.controlContainer) return null;
    return this.controlContainer.control?.get(this.formControlName) ?? null;
  }

  get isInvalid(): boolean {
    const ctrl = this.control;
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

}
